// Universal pose-slot model, shared by every product config flow (regular
// single products, multi-config bundles, BYO/YPX children).
//
// Each record describes a single pose, typed individual or group:
//   { type: 'individual' | 'group', label: string | null }
//
// Poses come from the backend `image_nodes` array (`PicsProduct#image_nodes`),
// which stores one `{ label, image_type }` node per pose for every image type
// (`no_image` nodes are skipped — nothing to pose). Backgrounds are NOT part of
// a record: they are an independent total (`background_options_count`) handled
// separately, so a product can have any number of poses and any number of
// backgrounds. Every serialized product carries `image_nodes` — the frontend
// never reconstructs slots from legacy `poses_count` / `image_type` fields.

export type ImageRecordType = 'individual' | 'group';

export interface ImageRecord {
  type: ImageRecordType;
  label: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Product = any;

const individualRecord = (label: string | null = null): ImageRecord => ({
  type: 'individual',
  label,
});

const groupRecord = (label: string | null = null): ImageRecord => ({
  type: 'group',
  label,
});

// One record per image node, typed by the node's own image_type; no_image nodes
// carry nothing to pose and are skipped.
const recordsFromNodes = (nodes: any[]): ImageRecord[] =>
  (nodes ?? []).flatMap((node: any): ImageRecord[] => {
    if (node?.image_type === 'portrait_image') {
      return [individualRecord(node?.label ?? null)];
    }
    if (node?.image_type === 'group_image') {
      return [groupRecord(node?.label ?? null)];
    }
    return []; // no_image nodes are not configurable
  });

// The universal pose-slot list for a product. Pose slots = records.length (each
// typed individual/group). Backgrounds are independent — see
// `backgroundSlotsCount`.
export const getImageRecords = (product: Product): ImageRecord[] =>
  recordsFromNodes(product?.image_nodes ?? []);

// Number of pose slots a product exposes.
export const posesCountFromRecords = (records: ImageRecord[]): number =>
  records.length;

// Product-level shorthand for the pose-slot count, symmetric with
// backgroundSlotsCount. Prefer this over composing
// posesCountFromRecords(getImageRecords(product)) unless the records list is
// already at hand.
export const productPosesCount = (product: Product): number =>
  posesCountFromRecords(getImageRecords(product));

// The number of independent background slots a product exposes. Poses and
// backgrounds are separate totals: a product may have more (or fewer)
// backgrounds than poses. Serialized payloads carry `background_options_count`;
// raw PicsProduct-shaped objects carry `backgrounds_count`.
export const backgroundSlotsCount = (product: Product): number =>
  Math.max(
    0,
    product?.background_options_count ?? product?.backgrounds_count ?? 0,
  );

// Whether any pose slot is an individual (croppable) pose. Pure group products
// (and prepay, handled separately) never show crop options.
export const hasIndividualRecord = (records: ImageRecord[]): boolean =>
  records.some((record) => record.type === 'individual');

// Number of individual pose slots — crop selections are required per
// individual record; group slots never take crops.
export const individualRecordsCount = (records: ImageRecord[]): number =>
  records.filter((record) => record.type === 'individual').length;

// Whether the product is a pure group product — at least one pose slot and
// every slot is a group photo. Mixed multiple-image products are excluded by
// their own individual nodes.
export const productIsAllGroup = (product: Product): boolean => {
  const records = getImageRecords(product);
  return (
    records.length > 0 && records.every((record) => record.type === 'group')
  );
};

// Whether any pose slot is a group photo — true for pure group products and
// multiple-image products that include a group node.
export const productHasGroupRecord = (product: Product): boolean =>
  getImageRecords(product).some((record) => record.type === 'group');
